import { PageHero, SectionHead, Reveal, Card, Eyebrow, usePageMeta } from "@/components/primitives";
import { Validation, Roadmap, FinalCTA } from "@/components/blocks2";
import Founders from "@/components/Founders";
import EnquiryForm from "@/components/EnquiryForm";

const THESIS = [
  { title: "Trust Is the Scarcest Resource", body: "India's digital finance has scaled transactions faster than it has scaled trust. Fraud, identity misuse and fragmented compliance are now the binding constraint — and the opportunity." },
  { title: "Infrastructure, Not Another App", body: "Durable value in FinTech accrues to rails and intelligence layers. TimePe is being built as the trust and intelligence layer beneath many journeys, not a single consumer app." },
  { title: "Bharat-First Distribution", body: "A tech-plus-touch model built from grassroots financial services experience gives TimePe a distribution DNA that purely digital players cannot easily replicate." },
];

const WHY_NOW = [
  "Digital public infrastructure has made real-time payments mainstream — risk, fraud and compliance capabilities must now catch up.",
  "Fraud sophistication is rising faster than legacy, rules-only controls can adapt.",
  "Regulators and institutions increasingly expect privacy and compliance by design, not by patch.",
];

const MODEL = [
  { title: "Platform & API Usage", body: "Usage-based economics across payments, banking and insurance journeys on shared infrastructure." },
  { title: "Intelligence Services", body: "Risk, fraud and compliance intelligence for banks, NBFCs, insurers and FinTechs." },
  { title: "Ecosystem Partnerships", body: "Distribution and co-build partnerships with institutions serving Bharat's last mile." },
];

const ADVANTAGE = [
  { title: "Unified vs Point Solutions", body: "One decisioning framework across payments, banking and insurance — instead of fragmented tools per service." },
  { title: "Compounding Intelligence", body: "Every journey improves the risk and fraud intelligence that protects the next one." },
  { title: "Grassroots to DeepTech", body: "Founding experience that spans BC networks, Aadhaar ecosystem work and CSC last-mile delivery through to AI/ML engineering." },
  { title: "Compliance-First Architecture", body: "Regulatory readiness is a structural property of the platform, lowering the cost of trust for every partner." },
];

const USE_OF_FUNDS = [
  { title: "Engineering & AI", body: "Core platform, intelligence layer and security engineering." },
  { title: "Security & Compliance", body: "Audits, certifications, regulatory alignment and privacy infrastructure." },
  { title: "Distribution & Partnerships", body: "Agent network, institutional integrations and ecosystem development." },
  { title: "Operations & Governance", body: "Team, processes and institutional-grade governance." },
];

function NumberedSection({ index, eyebrow, title, children, alt = false }) {
  return (
    <section className={`border-b border-line py-20 sm:py-24 ${alt ? "bg-abyss/50" : ""}`} data-testid={`investor-section-${index}`}>
      <div className="mx-auto max-w-[1440px] px-6 lg:px-8">
        <Reveal>
          <Eyebrow>{eyebrow}</Eyebrow>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight text-mist">{title}</h2>
        </Reveal>
        <div className="mt-10">{children}</div>
      </div>
    </section>
  );
}

export default function Investors() {
  usePageMeta(
    "TimePe Investors | Building India's Financial Infrastructure Layer",
    "Invest in the infrastructure behind Bharat's financial future — a compliance-first, AI-driven financial infrastructure platform at the intersection of FinTech, cybersecurity, RegTech and digital trust."
  );
  return (
    <>
      <PageHero
        eyebrow="Investors"
        lines={["Invest in the Infrastructure", "Behind Bharat's Financial Future."]}
        lead="TimePe is building a compliance-first, AI-driven financial infrastructure platform at the intersection of FinTech, cybersecurity, RegTech, financial inclusion and digital trust."
      />

      <NumberedSection index={1} eyebrow="Investment Thesis" title="Why TimePe">
        <div className="grid gap-5 lg:grid-cols-3">
          {THESIS.map((t, i) => (
            <Reveal key={t.title} delay={i * 0.07}>
              <Card testid={`thesis-card-${i}`} className="h-full p-7">
                <h3 className="font-display text-lg font-semibold text-mist">{t.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mute">{t.body}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </NumberedSection>

      <NumberedSection index={2} eyebrow="Why Now" title="The Window Is Open" alt>
        <div className="grid gap-5 lg:grid-cols-3">
          {WHY_NOW.map((w, i) => (
            <Reveal key={i} delay={i * 0.07}>
              <Card testid={`why-now-${i}`} className="h-full p-7">
                <p className="font-mono text-xs text-electric">{String(i + 1).padStart(2, "0")}</p>
                <p className="mt-4 text-sm leading-relaxed text-mist/90">{w}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </NumberedSection>

      <NumberedSection index={3} eyebrow="Market Opportunity" title="The Trust Gap in the World's Most Active Digital Finance Market">
        <Reveal>
          <p className="max-w-3xl text-base leading-relaxed text-mute">
            India operates one of the world's highest-volume real-time payment ecosystems, while a large
            share of users across Tier 2–6 markets still depends on assisted financial access. The gap
            between digital scale and trust infrastructure is precisely the layer TimePe is built to
            occupy. We share detailed market sizing and segmentation in the investor deck.
          </p>
        </Reveal>
      </NumberedSection>

      <NumberedSection index={4} eyebrow="Business Model" title="How TimePe Intends to Earn" alt>
        <div className="grid gap-5 lg:grid-cols-3">
          {MODEL.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.07}>
              <Card testid={`model-card-${i}`} className="h-full p-7">
                <h3 className="font-display text-lg font-semibold text-mist">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mute">{m.body}</p>
              </Card>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-6 text-xs leading-relaxed text-mute/80">
            Intended revenue model — being refined as the platform matures.
          </p>
        </Reveal>
      </NumberedSection>

      <NumberedSection index={5} eyebrow="Competitive Advantage" title="Built Different. Built for Bharat.">
        <div className="grid gap-5 sm:grid-cols-2">
          {ADVANTAGE.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.06}>
              <Card testid={`advantage-card-${i}`} className="h-full p-7">
                <h3 className="font-display text-lg font-semibold text-mist">{a.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mute">{a.body}</p>
              </Card>
            </Reveal>
          ))}
        </div>
      </NumberedSection>

      <Validation />
      <Roadmap chapter="06 — Roadmap" />

      <NumberedSection index={7} eyebrow="Funding / Use of Funds" title="Where Capital Goes" alt>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {USE_OF_FUNDS.map((u, i) => (
            <Reveal key={u.title} delay={i * 0.06}>
              <Card testid={`funds-card-${i}`} className="h-full p-7">
                <h3 className="font-display text-base font-semibold text-mist">{u.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-mute">{u.body}</p>
              </Card>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-6 text-xs leading-relaxed text-mute/80">
            Indicative allocation areas; the detailed breakdown is shared in the investor deck.
          </p>
        </Reveal>
      </NumberedSection>

      <Founders chapter="08 — Team" compact />

      <section className="py-24 sm:py-32" data-testid="investor-deck-section">
        <div className="mx-auto max-w-4xl px-6">
          <SectionHead eyebrow="Request the Investor Deck" title="Start a Conversation" className="mb-12" center />
          <EnquiryForm
            defaultInterest="Investment"
            source="investors-page"
            heading="Investor Deck Request"
            lead="Share your details and our team will follow up with the deck and a conversation."
            testid="investor-enquiry"
          />
        </div>
      </section>
    </>
  );
}
